import { Link } from "react-router-dom";
import { productKeywords, KeywordMapping } from "@/data/keywordContentMap";

interface InternalLinkInjectorProps {
  children: string;
  maxLinks?: number;
  className?: string;
  excludeUrls?: string[];
}

/**
 * Component that automatically injects internal links into text content
 * based on keyword mappings for SEO optimization
 */
export function InternalLinkInjector({ 
  children, 
  maxLinks = 3, 
  className = "",
  excludeUrls = []
}: InternalLinkInjectorProps) {
  if (!children || typeof children !== 'string') {
    return <span className={className}>{children}</span>;
  }

  // Find matching keywords in the content
  const contentLower = children.toLowerCase();
  const matches: KeywordMapping[] = [];
  
  for (const mapping of productKeywords) {
    if (
      contentLower.includes(mapping.keyword.toLowerCase()) && 
      matches.length < maxLinks &&
      !excludeUrls.includes(mapping.targetUrl) &&
      !matches.some(m => m.targetUrl === mapping.targetUrl)
    ) {
      matches.push(mapping);
    }
  }

  if (matches.length === 0) {
    return <span className={className}>{children}</span>;
  }

  // Sort by relevance and keyword length (longer keywords first to avoid partial matches)
  matches.sort((a, b) => {
    if (b.relevanceScore !== a.relevanceScore) {
      return b.relevanceScore - a.relevanceScore;
    }
    return b.keyword.length - a.keyword.length;
  });

  // Replace keywords with links
  let result = children;
  const replacements: { original: string; replacement: JSX.Element; index: number }[] = [];

  for (const match of matches) {
    // Find the exact case-insensitive match
    const regex = new RegExp(`\\b(${escapeRegExp(match.keyword)})\\b`, 'gi');
    const matchResult = regex.exec(result);
    
    if (matchResult) {
      const originalText = matchResult[1];
      const startIndex = matchResult.index;
      
      replacements.push({
        original: originalText,
        replacement: (
          <Link 
            key={`link-${match.keyword}`}
            to={match.targetUrl}
            className="text-primary hover:text-primary/80 underline underline-offset-2 transition-colors"
            title={match.anchorText}
          >
            {originalText}
          </Link>
        ),
        index: startIndex
      });
    }
  }

  // If no replacements found, return original
  if (replacements.length === 0) {
    return <span className={className}>{children}</span>;
  }

  // Build the result with links
  const parts: (string | JSX.Element)[] = [];
  let lastIndex = 0;

  // Sort replacements by index
  replacements.sort((a, b) => a.index - b.index);

  for (const rep of replacements) {
    // Add text before the match
    if (rep.index > lastIndex) {
      parts.push(children.substring(lastIndex, rep.index));
    }
    // Add the link
    parts.push(rep.replacement);
    lastIndex = rep.index + rep.original.length;
  }

  // Add remaining text
  if (lastIndex < children.length) {
    parts.push(children.substring(lastIndex));
  }

  return <span className={className}>{parts}</span>;
}

// Helper to escape regex special characters
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Hook to get internal link suggestions for content
 */
export function useInternalLinkSuggestions(content: string, maxLinks: number = 5): KeywordMapping[] {
  const contentLower = content.toLowerCase();
  const matches: KeywordMapping[] = [];
  
  for (const mapping of productKeywords) {
    if (contentLower.includes(mapping.keyword.toLowerCase()) && matches.length < maxLinks) {
      if (!matches.some(m => m.targetUrl === mapping.targetUrl)) {
        matches.push(mapping);
      }
    }
  }
  
  return matches.sort((a, b) => b.relevanceScore - a.relevanceScore);
}
